import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  UserGroupIcon,
  BuildingOfficeIcon,
  UserIcon,
  PlusIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import { apiService, RecipientTarget } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface PolicyRecipientSelectorProps {
  selectedRecipients: RecipientTarget[];
  onRecipientsChange: (recipients: RecipientTarget[]) => void;
  disabled?: boolean;
  autoOpenAddModal?: boolean;
  onClose?: () => void; // 🎯 NEW: Add onClose prop
}

const PolicyRecipientSelector: React.FC<PolicyRecipientSelectorProps> = ({
  selectedRecipients,
  onRecipientsChange,
  disabled = false,
  autoOpenAddModal = false,
  onClose, // 🎯 NEW: Destructure onClose
}) => {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedType, setSelectedType] = useState<'user_group' | 'company' | 'user'>('user_group');
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [userEmail, setUserEmail] = useState('');

  // Auto-open add modal if requested
  useEffect(() => {
    if (autoOpenAddModal) {
    setShowAddModal(true);
  }
  }, [autoOpenAddModal]);

  // Fetch user groups
  const { data: userGroupsResponse, isLoading: groupsLoading } = useQuery({
    queryKey: ['user-groups'],
    queryFn: () => apiService.getUserGroups(),
    enabled: !disabled,
  });

  // Extract groups array from response and ensure it's always an array
  const userGroups = userGroupsResponse?.groups && Array.isArray(userGroupsResponse.groups) ? userGroupsResponse.groups : [];

  // Filter groups based on search
  const filteredGroups = userGroups.filter(group =>
    group.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    group.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const hasCompany = !!user?.company_id;

  const handleAddRecipients = () => {
    const newRecipients: RecipientTarget[] = [];

    if (selectedType === 'user_group') {
      selectedItems.forEach(groupId => {
        newRecipients.push({
          type: 'user_group',
          user_group_id: groupId,
        });
      });
    } else if (selectedType === 'company' && hasCompany) {
      newRecipients.push({
        type: 'company',
        company_id: user!.company_id!,
      });
    } else if (selectedType === 'user' && userEmail.trim()) {
      // For individual users, we'll need to create a special target
      // This would require backend support for email-based user targeting
      newRecipients.push({
        type: 'user',
        user_id: userEmail.trim(), // This would need to be resolved to actual user ID
      });
    }

    // Add new recipients to existing ones
    const updatedRecipients = [...selectedRecipients, ...newRecipients];
    onRecipientsChange(updatedRecipients);
    
    // Reset form
    setSelectedItems([]);
    setUserEmail('');
    setShowAddModal(false);
  };

  const handleRemoveRecipient = (index: number) => {
    const updatedRecipients = selectedRecipients.filter((_, i) => i !== index);
    onRecipientsChange(updatedRecipients);
  };

  const getRecipientDisplayName = (recipient: RecipientTarget) => {
    switch (recipient.type) {
      case 'user_group':
        const group = userGroups.find(g => g.id === recipient.user_group_id);
        return group ? group.name : 'Unknown Group';
      case 'company':
        return 'Your Company';
      case 'user':
        return recipient.user_id || 'Unknown User';
      default:
        return 'Unknown';
    }
  };

  const getRecipientIcon = (type: string) => {
    switch (type) {
      case 'user_group':
        return <UserGroupIcon className="w-5 h-5" />;
      case 'company':
        return <BuildingOfficeIcon className="w-5 h-5" />;
      case 'user':
        return <UserIcon className="w-5 h-5" />;
      default:
        return <UserIcon className="w-5 h-5" />;
    }
  };

  const getRecipientColor = (type: string) => {
    switch (type) {
      case 'user_group':
        return 'bg-blue-100 text-blue-800';
      case 'company':
        return 'bg-green-100 text-green-800';
      case 'user':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-4">
      {/* Selected Recipients */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Policy Recipients
        </label>
        <div className="min-h-[100px] border border-gray-300 rounded-lg p-3 bg-gray-50">
        
            <div className="space-y-2">
              {selectedRecipients.map((recipient, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-2 bg-white rounded border"
                >
                  <div className="flex items-center space-x-2">
                    {getRecipientIcon(recipient.type)}
                    <span className="text-sm font-medium text-gray-900">
                      {getRecipientDisplayName(recipient)}
                    </span>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getRecipientColor(recipient.type)}`}>
                      {recipient.type.replace('_', ' ')}
                    </span>
                  </div>
                  {!disabled && (
                    <button
                      onClick={() => handleRemoveRecipient(index)}
                      className="text-gray-400 hover:text-red-600"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
       
        </div>
      </div>

      {/* Add Recipients Button - Only show when not auto-opening modal */}
      {!disabled && !autoOpenAddModal && (
        <button
          onClick={() => setShowAddModal(true)}
          className="w-full flex items-center justify-center px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-governate-500 hover:text-governate-600 transition-colors"
        >
          <PlusIcon className="w-5 h-5 mr-2" />
          Add Recipients
        </button>
      )}

      {/* Add Recipients Modal */}
      {showAddModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Add Policy Recipients</h3>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  if (onClose) {
                    onClose(); // 🎯 NEW: Call parent's onClose handler
                  }
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            {/* Recipient Type Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Select Recipient Type
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setSelectedType('user_group')}
                  className={`p-3 border rounded-lg text-left transition-colors ${
                    selectedType === 'user_group'
                      ? 'border-governate-500 bg-governate-50 text-governate-700'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <UserGroupIcon className="w-6 h-6 mb-2" />
                  <div className="font-medium">User Groups</div>
                  <div className="text-sm text-gray-500">Select from existing groups</div>
                </button>

                {hasCompany && (
                  <button
                    onClick={() => setSelectedType('company')}
                    className={`p-3 border rounded-lg text-left transition-colors ${
                      selectedType === 'company'
                        ? 'border-governate-500 bg-governate-50 text-governate-700'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <BuildingOfficeIcon className="w-6 h-6 mb-2" />
                    <div className="font-medium">Your Company</div>
                    <div className="text-sm text-gray-500">All company members</div>
                  </button>
                )}

                <button
                  onClick={() => setSelectedType('user')}
                  className={`p-3 border rounded-lg text-left transition-colors ${
                    selectedType === 'user'
                      ? 'border-governate-500 bg-governate-50 text-governate-700'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <UserIcon className="w-6 h-6 mb-2" />
                  <div className="font-medium">Individual User</div>
                  <div className="text-sm text-gray-500">Add by email address</div>
                </button>
              </div>
            </div>

            {/* Selection Content */}
            {selectedType === 'user_group' && (
              <div>
                <div className="mb-4">
                  <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      placeholder="Search groups..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-governate-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {!groupsLoading && (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {filteredGroups.map((group) => (
                      <label
                        key={group.id}
                        className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedItems.includes(group.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedItems([...selectedItems, group.id]);
                            } else {
                              setSelectedItems(selectedItems.filter(id => id !== group.id));
                            }
                          }}
                          className="h-4 w-4 text-governate-600 focus:ring-governate-500 border-gray-300 rounded"
                        />
                        <div className="ml-3 flex-1">
                          <div className="font-medium text-gray-900">{group.name}</div>
                          {group.description && (
                            <div className="text-sm text-gray-500">{group.description}</div>
                          )}
                          <div className="text-xs text-gray-400">
                            {group.member_count} members
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}

            {selectedType === 'user' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  User Email Address
                </label>
                <input
                  type="email"
                  value={userEmail}
                  onChange={(e) => setUserEmail(e.target.value)}
                  placeholder="user@example.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-governate-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Enter the email address of the user you want to add
                </p>
              </div>
            )}

            {selectedType === 'company' && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center">
                  <CheckIcon className="w-5 h-5 text-blue-600 mr-2" />
                  <div>
                    <div className="font-medium text-blue-900">Your Company</div>
                    <div className="text-sm text-blue-700">All members of your company will be included</div>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  if (onClose) {
                    onClose(); // 🎯 NEW: Call parent's onClose handler
                  }
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleAddRecipients}
                disabled={
                  (selectedType === 'user_group' && selectedItems.length === 0) ||
                  (selectedType === 'user' && !userEmail.trim()) ||
                  (selectedType === 'company' && !hasCompany)
                }
                className="px-4 py-2 text-sm font-medium text-white bg-governate-600 rounded-lg hover:bg-governate-700 disabled:opacity-50"
              >
                Add Recipients
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PolicyRecipientSelector;
